/**
 * 背景 AI 最適化結果の比較モーダル (Phase 34.1)
 * - 現在の画像と新規生成画像を横並びで表示
 * - 採用ボタンで slide.background.url を差し替え
 * - キャンセル/「現在を維持」で何もしない（新画像は Storage に残る）
 */
import { Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  currentUrl: string | null;
  newUrl: string | null;
  /** 採用ボタン押下時に呼ばれる（'current' は変更なし、'new' は new に差し替え） */
  onChoose: (choice: 'current' | 'new') => void;
  onClose: () => void;
}

export function BackgroundOptimizeCompareDialog({
  open,
  currentUrl,
  newUrl,
  onChoose,
  onClose,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>背景画像の比較</DialogTitle>
          <DialogDescription>
            現在の画像と AI が再生成した画像を比較し、どちらを採用するか選んでください。
            両方の画像は Storage に保存されています。
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          <div className="space-y-2">
            <div className="text-xs text-[var(--text-muted)] font-semibold">現在の画像</div>
            <div className="border border-[var(--border)] rounded bg-[var(--bg-elevated)] aspect-square overflow-hidden flex items-center justify-center">
              {currentUrl ? (
                <img
                  src={currentUrl}
                  alt="current"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-[var(--text-muted)] text-xs">現在の画像なし</span>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => onChoose('current')}
            >
              <X className="size-3 mr-1" /> 現在を維持
            </Button>
          </div>

          <div className="space-y-2">
            <div className="text-xs text-[var(--accent)] font-semibold">新しい画像（AI 生成）</div>
            <div className="border-2 border-[var(--accent)] rounded bg-[var(--bg-elevated)] aspect-square overflow-hidden flex items-center justify-center">
              {newUrl ? (
                <img src={newUrl} alt="new" className="w-full h-full object-contain" />
              ) : (
                <span className="text-[var(--text-muted)] text-xs">生成失敗</span>
              )}
            </div>
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={() => onChoose('new')}
              disabled={!newUrl}
            >
              <Check className="size-3 mr-1" /> 新しい画像を採用
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
